import { defaultLocalePath } from '~/constants/appLocale'
import { FEATURE_PAGE_PATHS } from '~/constants/featurePages'
import { GUIDE_PAGE_PATHS } from '~/constants/guidePages'
import { isPublicCvSlugPath } from '~/utils/publicCvLink'

/**
 * Routes that render without a session.
 *
 * Extracted from the two global middlewares, which each carried their own copy of
 * this list — and disagreed: /blog and /blog/** were public to auth.global but not
 * to profile-required.global, so every blog request ran a session lookup and a
 * `profiles` query it had no use for.
 */
const PUBLIC_PATHS = new Set([
  '/',
  '/login',
  '/registration',
  '/auth/callback',
  '/contact',
  '/terms',
  '/privacy',
  '/cookies',
  '/reset-password',
  '/demo',
  // Ad landing page: builds a CV with no account at all.
  '/free-cv-builder',
  // Iframe renderer for the builder's web CV preview.
  '/web-preview',
  '/blog',
  '/unsubscribe',
  // Affiliate program: the landing page for visitors (the dashboard once signed
  // in) and its terms, readable before joining and linked from partners' sites.
  '/affiliate',
  '/affiliate/terms',
  // Its audience pages (HR, recruiters, AI…) and their hub; the pages under it
  // are in PUBLIC_PREFIXES.
  '/affiliate-programs',
  // Marketing subpages carved out of the old one-page homepage.
  ...FEATURE_PAGE_PATHS,
  // Long-form guides. Public for the same reason the feature pages are: they
  // exist to be found by someone who has never heard of us.
  // The hubs /resume-synonyms, /resume-examples and /alternatives are guide
  // pages too; the pages generated under them are in PUBLIC_PREFIXES.
  ...GUIDE_PAGE_PATHS,
])

// /download/<token> and /claim/<token> are the guest buyer's own pages: the token
// is the credential, and the whole point is that there is no account behind it.
// They stay public for signed-in visitors too — a half-finished profile must not
// stand between someone and the purchase they have already paid for.
// /p/<code> and /embed/<code> are an affiliate partner's page and widget.
const PUBLIC_PREFIXES = ['/p/', '/embed/', '/blog/', '/cv/public', '/card/public', '/web/', '/download/', '/claim/', '/resume-synonyms/', '/resume-examples/', '/alternatives/', '/affiliate-programs/']

/** Takes a path in any language: /cs/pricing is as public as /pricing. */
export function isPublicPath(localizedPath: string): boolean {
  const path = defaultLocalePath(localizedPath)
  return PUBLIC_PATHS.has(path)
    || PUBLIC_PREFIXES.some(prefix => path.startsWith(prefix))
    // A shared CV under its slug; /cv/<uuid> next to it is the owner's editor
    || isPublicCvSlugPath(path)
}
